import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getDemo, getDemoPublicUrl } from "@/lib/api";
import { isAuthenticated } from "@/lib/auth";
import { Nav } from "@/components/Nav";
import { ArrowLeft, ExternalLink, Star, Copy, Sparkles } from "lucide-react";
import DEMO_SITES from "@/components/sites/demoSites";
import { toast } from "sonner";

export default function Demo() {
  const { demoId } = useParams();
  const isOperator = isAuthenticated();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  const slug = (d?.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const HandCoded = DEMO_SITES[slug] || null;

  useEffect(() => {
    getDemo(demoId).then(setD).catch(() => setErr(true));
  }, [demoId, setD, setErr]);

  // Poll every 6s while HTML is still being generated
  const pollRef = useRef(null);
  useEffect(() => {
    if (d && !HandCoded && !d.html) {
      pollRef.current = setInterval(() => {
        getDemo(demoId).then(setD).catch(() => {});
      }, 6000);
    }
    return () => clearInterval(pollRef.current);
  }, [d, HandCoded, demoId]);

  if (err) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-display text-5xl">Demo not found</h1>
        <Link to="/bulk" className="mt-6 underline text-[#FF3B30] inline-block">← Back to candidates</Link>
      </div>
    </div>
  );

  if (!d) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 py-32 font-mono text-white/40">building case…</div>
    </div>
  );

  const bp = d.blueprint || null;
  const business = {
    name: d.name,
    address: d.address || "Address on file",
    phone: d.phone || "",
    rating: d.rating ?? null,
    reviews: d.reviews ?? 0,
  };
  const verdict = bp?.brand?.essence ||
    `You've built a ${d.rating ?? "great"}★ reputation across ${d.reviews ?? "plenty of"} reviews${d.website ? "" : " — without a website to show for it yet"}.`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Nav />

      {/* Header */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
        {isOperator && (
          <Link to="/bulk" className="inline-flex items-center gap-2 font-mono text-xs text-white/50 hover:text-[#FF3B30] mb-12">
            <ArrowLeft className="w-3 h-3" /> ALL CANDIDATES
          </Link>
        )}

        <div className="flex items-center gap-3 mb-4">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30]">
            <Sparkles className="w-3 h-3" /> BESPOKE PREVIEW
          </span>
          <button
            data-testid="demo-copy-link"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Link copied · ready to share");
              } catch {
                toast.error("Copy unavailable — check browser permissions");
              }
            }}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-white border border-white/15 px-2 py-1 inline-flex items-center gap-1"
          >
            <Copy className="w-3 h-3" /> COPY LINK
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">
              {(d.borough || "LONDON").toUpperCase()} · {d.trade.toUpperCase()}
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95]">{d.name}</h1>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-6 font-mono text-xs">
            <div>
              <div className="text-3xl font-bold text-white">{d.rating ?? "—"}<span className="text-[#FF3B30]">★</span></div>
              <div className="text-white/50 uppercase tracking-[0.2em] text-[10px] mt-1">Google rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">{d.reviews ?? 0}</div>
              <div className="text-white/50 uppercase tracking-[0.2em] text-[10px] mt-1">Reviews</div>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE */}
      <section className="border-y border-white/10 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">ABOUT YOUR BUSINESS</p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">Where you're starting from.</h2>
              <p className="text-white/60 mt-4 text-sm">Based on your live Google Business listing.</p>
            </div>
            <div className="md:col-span-8">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Rating" value={d.rating ? `${d.rating}★` : "—"} />
                <Stat label="Reviews" value={d.reviews ?? 0} />
                <Stat label="Borough" value={d.borough || "London"} />
                <Stat label="Current site" value={d.website ? "Yes" : "None yet"} accent={!d.website} />
              </div>
              <div className="mt-6 flex items-start gap-3 text-white/70">
                <Star className="w-4 h-4 text-[#FF3B30] mt-1 flex-shrink-0" />
                <p className="text-base md:text-lg leading-relaxed">{verdict}</p>
              </div>
              {d.website && (
                <a href={d.website} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 font-mono text-xs text-white/50 hover:text-[#FF3B30]">
                  Current site <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {Array.isArray(d.review_snippets) && d.review_snippets.length > 0 && (
                <div className="mt-8 border border-white/10 bg-[#0a0a0a] p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">WHAT PEOPLE ARE ALREADY SAYING ABOUT YOU</p>
                  <ul className="space-y-2 text-white/60 text-sm">
                    {d.review_snippets.slice(0, 4).map((s) => (
                      <li key={s.slice(0, 60)} className="border-l border-[#FF3B30]/40 pl-3">{s}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* THE BRIEF — pitch cards */}
      {Array.isArray(bp?.brief) && bp.brief.length > 0 && (
        <section data-testid="demo-brief" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">THE BRIEF</p>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl mb-10">
            What we'd build for you.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {bp.brief.slice(0, 4).map((item, i) => (
              <div key={`${i}-${item.slice(0, 24)}`} className="border border-white/10 p-6 bg-[#111] hover:border-[#FF3B30] transition-colors">
                <div className="font-mono text-xs text-[#FF3B30] mb-3">0{i + 1}</div>
                <p className="text-white/80 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AFTER — Bespoke generated site */}
      <section data-testid="demo-site" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF3B30] mb-4">AFTER</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter">The website you should have.</h2>
            </div>
            <div className="hidden md:flex items-center gap-2 font-mono text-xs text-white/40">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              LIVE PROTOTYPE
            </div>
          </div>
          <p className="text-white/60 max-w-2xl">
            Designed specifically for you — colour palette, typography and copy all chosen around your real reviews.
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {HandCoded ? (
            /* Hand-coded polished site — renders inline */
            <div className="border border-white/15 bg-[#1a1a1a]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                <span className="w-3 h-3 rounded-full bg-[#FF5F57]"></span>
                <span className="w-3 h-3 rounded-full bg-[#FEBC2E]"></span>
                <span className="w-3 h-3 rounded-full bg-[#28C840]"></span>
                <div className="ml-4 flex-1 font-mono text-xs text-white/50 bg-black/40 px-3 py-1 truncate">
                  https://{(d.name || "demo").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.studio
                </div>
              </div>
              <HandCoded b={business} />
            </div>
          ) : d.html ? (
            /* Generated HTML site — embedded inline, same as the case-study AFTER pattern */
            <div className="border border-white/15 bg-[#1a1a1a]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
                <span className="w-3 h-3 rounded-full bg-[#FF5F57]"></span>
                <span className="w-3 h-3 rounded-full bg-[#FEBC2E]"></span>
                <span className="w-3 h-3 rounded-full bg-[#28C840]"></span>
                <div className="ml-4 flex-1 font-mono text-xs text-white/50 bg-black/40 px-3 py-1 truncate">
                  https://{(d.name || "demo").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.studio — generated prototype
                </div>
              </div>
              <iframe
                title={`${d.name} generated site`}
                src={getDemoPublicUrl(d)}
                className="w-full"
                style={{ height: "85vh", border: "none", display: "block" }}
              />
              <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-white/10 bg-[#111]">
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/30">Scroll inside the frame above · full site, live</p>
                <a
                  href={getDemoPublicUrl(d)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-mono text-xs text-white/60 hover:text-[#FF3B30] flex-shrink-0"
                >
                  Open full-screen <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          ) : (
            /* Still generating */
            <div className="border border-white/15 bg-[#111] p-16 text-center">
              <div className="flex justify-center gap-2 mb-8">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: "#FF3B30", animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }}
                  />
                ))}
              </div>
              <p className="font-display text-2xl mb-3">Building your site…</p>
              <p className="font-mono text-xs uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.4)" }}>
                Almost ready · this page refreshes automatically
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h3 className="font-display text-3xl md:text-5xl tracking-tighter mb-6">
          Like what you see? <span className="text-[#FF3B30]">Let's talk.</span>
        </h3>
        <p className="text-white/60 max-w-xl mx-auto mb-8">
          Reply to whoever sent you this, or reach the studio directly — we'll take it from here.
        </p>
        <a
          href="mailto:hello@the-managerly.com"
          className="inline-flex items-center gap-2 bg-[#FF3B30] text-white px-8 py-4 font-display font-bold hover:bg-[#E6352B] transition-colors"
        >
          hello@the-managerly.com
        </a>
      </section>

      <footer className="border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="The Managerly" className="h-6 w-auto" />
          </div>
          <p>London E2 / SE15 · hello@the-managerly.com</p>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="border border-white/10 bg-black px-4 py-3">
    <div className={`font-display text-xl ${accent ? "text-[#FF3B30]" : "text-white"}`}>{value}</div>
    <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40 mt-1">{label}</div>
  </div>
);
